import {
  MAX_PROFILE_NAME_LENGTH,
  TEST_MODE_NETWORKS,
  type TestModeConfig,
  type TestModeNetwork,
  type TestModeState,
} from '@/domain/testMode';
import {
  among,
  boolean,
  field,
  maxLength,
  minLength,
  minValue,
  numberWith,
  object,
  optionalField,
  stringWith,
  validate,
} from '@/lib/utils/validator/validator';

/** Ids come from GLaDOS; long enough for a UUID, bounded so a blob cannot carry a payload. */
const identifier = stringWith(minLength(1), maxLength(128));

/**
 * Bounded for the same reason the ids are: the value arrives from host-page
 * writable storage, is persisted, and is rendered in the test-mode banner. The
 * bound itself is the shared one — a driver that accepts a longer name would
 * forward one this rejects, failing the whole enter over a display string.
 */
const displayName = stringWith(maxLength(MAX_PROFILE_NAME_LENGTH));

/**
 * Rebuild a persisted `testMode` value into a trusted {@link TestModeState}, or
 * null when it does not conform — corrupted storage, or a blob written by a
 * widget version with a different shape.
 *
 * localStorage is writable by the host page, and this value decides which chat
 * transport the widget boots against, so a non-conforming value must boot as
 * production rather than as a half-populated test mode.
 *
 * Deliberately does not apply the TTL: the boot decision owns expiry, so there
 * is exactly one place that can retire a test session.
 */
export function toRehydratedTestModeState(value: unknown): TestModeState | null {
  try {
    const state = validate(value, (v) =>
      object(v, () => ({
        active: field('active', boolean),
        enteredAt: optionalField('enteredAt', numberWith(minValue(0))),
        network: optionalField('network', validTestModeNetwork),
        orgId: optionalField('orgId', identifier),
        profileId: optionalField('profileId', identifier),
        profileName: optionalField('profileName', displayName),
        sessionId: optionalField('sessionId', identifier),
      })),
    );
    // An active mode with nothing to test is not a lesser test mode, it is a
    // broken one: the boot decision would build a tester gateway with no
    // profile to send to, leaving the widget wired to an API it cannot call.
    if (state.active && (state.orgId === undefined || state.profileId === undefined)) {
      return null;
    }
    return state;
  } catch {
    return null;
  }
}

/**
 * Validate the configuration a driver passes to `_enterTestMode`. Throws a
 * `ValidationError` on a bad config — unlike a persisted blob, a caller that
 * gets this wrong should hear about it rather than silently get production.
 */
export function validTestModeConfig(value: unknown): TestModeConfig {
  // `validate` is what turns reported issues into a thrown ValidationError; a
  // bare `object()` collects them and hands back `invalid()`, which would reach
  // the caller as an undefined config typed as a valid one.
  return validate(value, (v) =>
    object(v, () => ({
      network: optionalField('network', validTestModeNetwork),
      orgId: field('orgId', identifier),
      profileId: field('profileId', identifier),
      profileName: optionalField('profileName', displayName),
    })),
  );
}

/**
 * `direct` bypasses the driver relay: the widget fetches the tester API itself,
 * with whatever origin a relative path resolves to. Accepting it in a production
 * build would point a real widget's chat transport at a path on the customer's
 * own origin, so outside dev builds the value does not exist.
 *
 * Necessary but not sufficient: build mode is a proxy for "this page is served
 * by the dev server", and the two come apart in `dev:extension`, which builds
 * with `--mode development` and injects the result into arbitrary sites. The
 * channel that performs the fetch is where the page's actual origin can be
 * checked, so it owns that half.
 *
 * The build mode is read per call, not at module scope, so the production path
 * is reachable from a test.
 */
function validTestModeNetwork(value: unknown): TestModeNetwork {
  const allowed: readonly TestModeNetwork[] = import.meta.env.DEV ? TEST_MODE_NETWORKS : ['channel'];
  return among(allowed)(value);
}
